import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Subject, Subscription, takeUntil, firstValueFrom } from 'rxjs';
import { BackOfficeService } from '../../../../services/backoffice.service';
import { AdditionalServicePaginationModel } from '../../../../models/orderModel';
import { MyConfigOrganization } from '../../../../models/myConfig';
import { BackOffice24StatesService } from '../state/back-office24-states.service';
import { DatePipe } from '@angular/common';
import { AdditionalServicesFormComponent } from '../back-office24-pricing/additional-services-form/additional-services-form.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BackOffice24OrdersEditAdditionalServiceComponent } from '../back-office24-orders/back-office24-orders-edit-additional-service/back-office24-orders-edit-additional-service.component';
import { UnplannedTasksModalComponent } from './unplanned-tasks-modal/unplanned-tasks-modal.component';
import { ScheduleTemplatesModalComponent } from './schedule-templates-modal/schedule-templates-modal.component';
import { TranslocoService } from '@jsverse/transloco';

interface DayData {
  date: Date;
  dayName: string;
  dayNameSv: string;
  dayNumber: number;
  orders: any[];
  isToday: boolean;
  templateStatus: any;
  templateStatusKey?: string;
  capacity?: number;
}

interface WeekData {
  startDate: Date;
  endDate: Date;
  days: DayData[];
}

@Component({
  selector: 'app-additional-service-planning',
  standalone: false,
  templateUrl: './additional-service-planning.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./additional-service-planning.component.css']
})
export class AdditionalServicePlanningComponent implements OnInit, OnDestroy {
  @Input() organizationId: string;
  @Output() incrementRequests: EventEmitter<void> = new EventEmitter<void>();
  @Output() decrementRequests: EventEmitter<void> = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  private orgSubscription: Subscription = new Subscription();

  // Organization
  organization: MyConfigOrganization;

  // Data
  allData: any[] = [];
  additionalServices: any[] = [];
  allAdditionalServices = new Map<string, any>();
  selectedServiceId: string | null = null;
  unplannedTasks: any[] = [];
  scheduleAssignments = new Map<string, any[]>();
  additionalServiceTemplates: any[] = [];
  availableTimeslots: any[] = [];
  dailyCapacities = new Map<string, number>();

  // Calendar data
  currentWeek: WeekData;
  isLoading = false;
  activeRequests = 0;
  private componentDestroyed$ = new Subject<void>();

  // Week navigation
  private currentWeekStart: Date;

  constructor(
    private backofficeService: BackOfficeService,
    private backOffice24StatesService: BackOffice24StatesService,
    public dialog: MatDialog,
    public datepipe: DatePipe,
    private snackBar: MatSnackBar,
    public translate: TranslocoService
  ) {
    this.currentWeekStart = this.getStartOfWeek(new Date());
  }

  ngOnInit(): void {
    this.orgSubscription = this.backOffice24StatesService.currentOrganization$
      .pipe(takeUntil(this.destroy$))
      .subscribe(org => {
        this.organization = org;
        if (org) {
          this.loadData().then(() => {
            this.loadUnplannedTasks().then(tasks => {
              this.unplannedTasks = tasks;
            });
          });
        }
      });
  }

  // Week navigation methods
  private getStartOfWeek(date: Date): Date {
    // Convert to Swedish timezone first
    const swedishDate = new Date(date.toLocaleString("en-US", { timeZone: "Europe/Stockholm" }));
    const day = swedishDate.getDay();
    const diff = swedishDate.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
    const startOfWeek = new Date(swedishDate.setDate(diff));
    return startOfWeek;
  }

  private generateWeekData(startDate: Date): WeekData {
    const days: DayData[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday

      const dayData: DayData = {
        date: new Date(date),
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNameSv: date.toLocaleDateString('sv-SE', { weekday: 'short' }),
        dayNumber: date.getDate(),
        orders: [],
        isToday: date.getTime() === today.getTime(),
        templateStatus: isWeekend ? 'closed' : 'workDay',
        templateStatusKey: isWeekend ? 'closed' : 'workDay'
      };
      days.push(dayData);
    }

    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);

    return {
      startDate: new Date(startDate),
      endDate,
      days
    };
  }

  // Navigation methods
  goToPreviousWeek(): void {
    this.currentWeekStart.setDate(this.currentWeekStart.getDate() - 7);
    this.loadData();
  }

  goToNextWeek(): void {
    this.currentWeekStart.setDate(this.currentWeekStart.getDate() + 7);
    this.loadData();
  }

  goToCurrentWeek(): void {
    this.currentWeekStart = this.getStartOfWeek(new Date());
    this.loadData();
  }

  loadScheduleTemplates(): void {
    const serviceId = this.selectedServiceId;
    if (serviceId) {
      this.backofficeService.getScheduleTemplates(serviceId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result) => {
            this.additionalServiceTemplates = result;
          },
          error: (error) => {
            console.error('Error loading schedule template:', error);
          }
        });
    }
  }

  private extractTemplateKey(name: any): string {
    if (!name) return '';
    if (typeof name === 'object') {
      return (name.en || name.sv || JSON.stringify(name));
    }
    if (typeof name === 'string') {
      try {
        const parsed = JSON.parse(name);
        if (parsed && typeof parsed === 'object') {
          return parsed.en || parsed.sv || name;
        }
      } catch (e) {
      }
      return name;
    }

    return String(name);
  }

  public getTemplateKey(nameOrDay: any): string {
    if (!nameOrDay) return '';
    if (nameOrDay.templateStatus !== undefined) {
      return this.extractTemplateKey(nameOrDay.templateStatus);
    }
    return this.extractTemplateKey(nameOrDay);
  }

  public getTemplateLabel(name: any): string {
    if (!name) return '';
    const lang = this.translate.getActiveLang ? this.translate.getActiveLang() : 'en';
    if (typeof name === 'object') {
      return name[lang] || name.en || name.sv || JSON.stringify(name);
    }
    if (typeof name === 'string') {
      try {
        const parsed = JSON.parse(name);
        if (parsed && typeof parsed === 'object') {
          return parsed[lang] || parsed.en || parsed.sv || name;
        }
      } catch (e) {
      }
      const lower = name.toLowerCase();
      if (lower == 'workday') {
        return lang === 'sv' ? 'arbetsdag' : 'workDay';
      }
      if (lower == 'closed') {
        return lang == 'sv' ? 'stängt' : 'closed';
      }
      return name;
    }
    return String(name);
  }

  // Data loading methods
  private createPaginationParams(statuses: number[], calculatePlannedDates: boolean = true): Partial<AdditionalServicePaginationModel> {
    let weekEnd: Date | null = null;
    let fromDate: Date | null = null;

    if (calculatePlannedDates) {
      weekEnd = new Date(this.currentWeekStart);
      weekEnd.setDate(this.currentWeekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      fromDate = new Date(this.currentWeekStart);
      fromDate.setHours(0, 0, 0, 0);
    } else {
      fromDate = null;
      weekEnd = null;
    }

    return {
      Planned: {
        From: fromDate ? this.datepipe.transform(fromDate, 'yyyy-MM-ddTHH:mm:ss') : null,
        To: weekEnd ? this.datepipe.transform(weekEnd, 'yyyy-MM-ddTHH:mm:ss') : null
      },
      AdditionalServiceIds: this.additionalServices.filter(e => e.id !== "e22b31a3-4ee6-4c8f-a980-b2fe00c1aefe").map(e => e.id),
      Statuses: statuses,
      SortingColumn: 'Planned',
      SortingDirection: 'asc',
      Paginate: {
        PerPage: 1000, // Get all for the week
        Page: 1
      }
    };
  }

  private async loadAllPages(organizationId: string, initialParams: Partial<AdditionalServicePaginationModel>): Promise<any[]> {
    let allData: any[] = [];
    let currentPage = 1;
    const pageSize = 100;
    let hasMoreData = true;

    while (hasMoreData) {
      const params = {
        ...initialParams,
        Paginate: {
          ...initialParams.Paginate,
          PerPage: pageSize,
          Page: currentPage
        }
      };

      try {
        const response = await firstValueFrom(
          this.backofficeService
            .getPaginatedAdditionalServiceTasks(organizationId, params)
            .pipe(takeUntil(this.destroy$))
        );

        if (!response || !response.data || response.data.length === 0) {
          hasMoreData = false;
        } else {
          allData = [...allData, ...response.data];
          const totalItems = response.pagination?.total || 0;
          const totalPages = Math.ceil(totalItems / pageSize);
          hasMoreData = currentPage < totalPages;
          currentPage++;
        }
      } catch (error) {
        console.error('Error loading page', currentPage, ':', error);
        hasMoreData = false;
      }
    }
    return allData;
  }

  private async loadUnplannedTasks() {
    const params = this.createPaginationParams([1, 2], false);
    return await this.loadAllPages(this.organization.organizationId, params);
  }

  private async populateServicesPerDay(services: any[]): Promise<any[]> {
    if (!services) return [];

    // Get current time in Swedish timezone
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);

    // Convert to Swedish timezone and override time
    const fromDate = this.toSwedishISOString(today).replace(/T\d{2}:\d{2}:\d{2}\.\d{3}/, 'T00:00:00.000');
    const toDate = this.toSwedishISOString(nextWeek).replace(/T\d{2}:\d{2}:\d{2}\.\d{3}/, 'T23:59:59.999');

    try {
      const timeslots = await firstValueFrom(
        this.backofficeService
          .getAdditionalServicesTimeslots(this.organization.organizationId, fromDate, toDate)
      );

      const serviceCapacityCounts = new Map<string, number>();

      services.forEach(service => {
        const serviceData = timeslots.find(s => s.serviceId === service.id);
        if (!serviceData?.slots) {
          serviceCapacityCounts.set(service.id, 0);
          return;
        }

        const capacityByDate = new Map<string, number>();
        serviceData.slots.forEach(slot => {
          const date = slot.localTime.split(' ')[0];
          const currentCapacity = capacityByDate.get(date) || 0;
          capacityByDate.set(date, currentCapacity + (slot.capacity || 0));
        });

        const capacities = Array.from(capacityByDate.values());
        const avgCapacity = capacities.length > 0
          ? Math.ceil(capacities.reduce((sum, cap) => sum + cap, 0) / capacities.length)
          : 0;
        serviceCapacityCounts.set(service.id, avgCapacity);
      });

      return services.map(service => ({
        ...service,
        servicesPerDay: serviceCapacityCounts.get(service.id) || 0
      }));
    } catch (error) {
      console.error('Error calculating servicesPerDay:', error);
      return services.map(service => ({
        ...service,
        servicesPerDay: 0
      }));
    }
  }

  private loadAdditionalServices(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.organization?.organizationId) {
        console.warn('No organization ID available to fetch additional services');
        resolve();
        return;
      }

      this.incrementRequests.emit();
      this.backofficeService
        .getOrganizationAdditionalServices(this.organization.organizationId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async (services) => {
            // Populate allAdditionalServices Map with all services (unfiltered)
            this.allAdditionalServices.clear();
            services.forEach(service => {
              if (service.id) {
                this.allAdditionalServices.set(service.id, service);
              }
            });

            // Filter and process services for display
            const filteredServices = services.filter(service => service.id !== "e22b31a3-4ee6-4c8f-a980-b2fe00c1aefe");
            this.additionalServices = await this.populateServicesPerDay(filteredServices) || [];
            resolve();
          },
          error: (error) => {
            console.error('Error loading additional services:', error);
            this.additionalServices = [];
            resolve();
          },
          complete: () => {
            this.decrementRequests.emit();
          }
        });
    });
  }


  private updateServiceCounts(): void {

    this.additionalServices.forEach(service => {
      service.ordersCount = 0;
    });

    this.allData.forEach(item => {
      const serviceId = item.additionalServiceId;
      const service = this.additionalServices.find(service => service.id === serviceId);
      if (serviceId && service) {
        service.ordersCount++;
      }
    });
  }

  private groupDataByDate(data: any[]): void {
    this.currentWeek = this.generateWeekData(this.currentWeekStart);

    this.currentWeek.days.forEach(day => {
      const dayStart = new Date(day.date);
      dayStart.setHours(0, 0, 0, 0);

      const dayEnd = new Date(day.date);
      dayEnd.setHours(23, 59, 59, 999);

      day.orders = data.filter(item => {
        if (!item.planned) return false;

        const plannedDate = new Date(item.planned);
        return plannedDate >= dayStart && plannedDate <= dayEnd;
      }).sort((a, b) => {
        return new Date(a.planned).getTime() - new Date(b.planned).getTime();
      });
    });
  }

  private toSwedishISOString(date: Date): string {
    const formatter = new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Europe/Stockholm',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(date);
    const partsMap: any = {};
    parts.forEach(part => {
      partsMap[part.type] = part.value;
    });

    const offset = this.getSwedishTimezoneOffset(date);

    return `${partsMap.year}-${partsMap.month}-${partsMap.day}T${partsMap.hour}:${partsMap.minute}:${partsMap.second}.000${offset}`;
  }

  private getSwedishOffsetMinutes(date: Date): number {
    // Check if date is in DST period for Sweden
    const year = date.getFullYear();
    const dstStart = new Date(year, 2, 31 - new Date(year, 2, 31).getDay()); // Last Sunday in March
    const dstEnd = new Date(year, 9, 31 - new Date(year, 9, 31).getDay()); // Last Sunday in October

    const isDST = date >= dstStart && date < dstEnd;
    return isDST ? 120 : 60; // +2 hours in summer, +1 hour in winter
  }

  private getSwedishTimezoneOffset(date: Date): string {
    const offsetMinutes = this.getSwedishOffsetMinutes(date);
    const offsetHours = Math.floor(offsetMinutes / 60);
    const offsetMins = offsetMinutes % 60;

    return `+${offsetHours.toString().padStart(2, '0')}:${offsetMins.toString().padStart(2, '0')}`;
  }

  // Handle service selection change
  onServiceChange(serviceId: string): void {
    this.selectedServiceId = serviceId;
    this.calculateDailyCapacities();
    this.groupDataByDate(this.allData.filter(item => item.additionalServiceId === this.selectedServiceId));
    this.loadScheduleAssignments();
    this.loadScheduleTemplates();
  }

  // Load schedule assignments for a service
  private loadScheduleAssignments(): void {
    const serviceId = this.selectedServiceId
    if (!serviceId) return;

    this.backofficeService.getScheduleAssignment(serviceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (assignments) => {
          this.scheduleAssignments.set(serviceId, assignments || []);
          this.updateDayActiveStates();
        },
        error: (error) => {
          console.error('Error loading schedule assignments:', error);
          this.scheduleAssignments.set(serviceId, []);
        }
      });
  }

  // Update day active states based on schedule assignments
  private updateDayActiveStates(): void {
    if (!this.selectedServiceId || !this.currentWeek) return;

    const assignments = this.scheduleAssignments.get(this.selectedServiceId) || [];
    this.currentWeek.days.forEach(day => {
      const dayOfWeek = day.date.getDay(); // 0 = Sunday, 6 = Saturday
      const dateString = day.date.toISOString().split('T')[0]; // YYYY-MM-DD format

      // Check if there's a specific date assignment
      const dateAssignment = assignments.find(a => a.assignment === dateString);
      if (dateAssignment) {
        day.templateStatus = dateAssignment.templateName;
        day.templateStatusKey = this.extractTemplateKey(dateAssignment.templateName);
        return;
      }

      // Check for day of week assignments
      const dayOfWeekAssignment = assignments.find(a =>
        a.repeat === 'DayOfWeek' &&
        new Date(a.assignment).getDay() === dayOfWeek
      );

      if (dayOfWeekAssignment) {
        day.templateStatus = dayOfWeekAssignment.templateName;
        day.templateStatusKey = this.extractTemplateKey(dayOfWeekAssignment.templateName);
      } else {
        const defaultKey = (dayOfWeek !== 0 && dayOfWeek !== 6) ? 'workDay' : 'closed';
        day.templateStatus = defaultKey;
        day.templateStatusKey = defaultKey;
      }
    });
  }

  onScheduleTemplateSelected(day: DayData, templateStatus: any): void {
    if (!this.selectedServiceId) return;

    const templateKey = this.extractTemplateKey(templateStatus);

    // Get the new template capacity
    const newTemplateCapacity = this.getTemplateCapacity(day.date, templateKey);
    const currentOrdersCount = day.orders.length;

    // Check if new capacity is less than current orders
    if (newTemplateCapacity < currentOrdersCount) {
      this.snackBar.open(
        `Please reschedule orders first. Current orders (${currentOrdersCount}) exceed the capacity of ${this.getTemplateLabel(templateStatus)} (${newTemplateCapacity}).`,
        'Warning',
        {
          verticalPosition: 'top',
          duration: 5000
        }
      );
      return;
    }

    const dateString = day.date.toISOString().split('T')[0];
    const assignments = this.scheduleAssignments.get(this.selectedServiceId) || [];
    const existingAssignment = assignments.find(a => a.assignment === dateString);
    const templateNameToSend = (typeof templateStatus === 'object') ? JSON.stringify(templateStatus) : templateStatus;

    const setDayTemplate = () => {
      day.templateStatus = templateStatus;
      day.templateStatusKey = templateKey;
    };

    if (existingAssignment) {
      this.backofficeService.deleteScheduleAssignment(existingAssignment.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            const model = {
              AdditionalServiceId: this.selectedServiceId,
              TemplateName: templateNameToSend,
              Assignment: dateString,
              Repeat: 'Manual'
            };

            this.backofficeService.createScheduleAssignment(model)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  setDayTemplate();
                  this.loadScheduleAssignments();
                },
                error: (error) => {
                  console.error('Error creating schedule assignment:', error);
                }
              });
          },
          error: (error) => {
            console.error('Error deleting schedule assignment:', error);
          }
        });
    } else {
      const model = {
        AdditionalServiceId: this.selectedServiceId,
        TemplateName: templateNameToSend,
        Assignment: dateString,
        Repeat: 'Manual'
      };

      this.backofficeService.createScheduleAssignment(model)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            setDayTemplate();
            this.loadScheduleAssignments();
          },
          error: (error) => {
            console.error('Error updating schedule assignment:', error);
          }
        });
    }
  }

  viewDetails(item: any): void {
    if (item && item.orderId) {
      window.open(`admin/back-office24-order/${item.orderId}`, '_blank');
    }
  }

  // Get formatted week range for display
  getWeekRangeText(): string {
    if (!this.currentWeek) return '';

    const startMonth = this.currentWeek.startDate.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = this.currentWeek.endDate.toLocaleDateString('en-US', { month: 'short' });
    const startDay = this.currentWeek.startDate.getDate();
    const endDay = this.currentWeek.endDate.getDate();
    const year = this.currentWeek.startDate.getFullYear();

    if (startMonth === endMonth) {
      return `${startMonth} ${startDay} - ${endDay}, ${year}`;
    } else {
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
    }
  }

  async loadData(getPlannedServiceData: boolean = true, getASData: boolean = false): Promise<void> {
    if (this.isLoading || !this.organization) return;

    this.incrementRequests.emit();
    this.isLoading = true;

    try {
      if (!this.additionalServices.length || getASData) {
        await this.loadAdditionalServices();
      }

      if ((!this.selectedServiceId && this.additionalServices.length > 0) || getASData) {
        this.selectedServiceId = this.additionalServices[0].id;
      }

      // Load available timeslots
      await this.loadAvailableTimeslots();

      // Load data for the current week and selected service
      const initialParams = this.createPaginationParams([4]);

      if (getPlannedServiceData) {
        let allData = await this.loadAllPages(this.organization.organizationId, initialParams);
        this.allData = allData;
      }

      this.updateServiceCounts();
      this.groupDataByDate(this.allData.filter(item => item.additionalServiceId === this.selectedServiceId));

      // Load schedule assignments for the selected service
      if (this.selectedServiceId) {
        this.loadScheduleAssignments();
        this.loadScheduleTemplates();
      }
    } catch (error) {
      console.error('Error loading additional service tasks:', error);
      this.allData = [];
      this.currentWeek = this.generateWeekData(this.currentWeekStart);
    } finally {
      this.isLoading = false;
      this.decrementRequests.emit();
    }
  }

  private async loadAvailableTimeslots(): Promise<void> {
    // Work directly with Swedish timezone dates
    const fromDateStart = new Date(this.currentWeekStart);
    const weekEnd = new Date(this.currentWeekStart);
    weekEnd.setDate(this.currentWeekStart.getDate() + 6);

    // Convert to Swedish timezone strings with proper time
    const fromDate = this.toSwedishISOString(fromDateStart).replace(/T\d{2}:\d{2}:\d{2}\.\d{3}/, 'T00:00:00.000');
    const toDate = this.toSwedishISOString(weekEnd).replace(/T\d{2}:\d{2}:\d{2}\.\d{3}/, 'T23:59:59.999');

    try {
      this.availableTimeslots = await firstValueFrom(
        this.backofficeService
          .getAdditionalServicesTimeslots(this.organization.organizationId, fromDate, toDate)
          .pipe(takeUntil(this.destroy$))
      );

      this.calculateDailyCapacities();
    } catch (error) {
      console.error('Error loading timeslots:', error);
      this.availableTimeslots = [];
    }
  }

  private calculateDailyCapacities(): void {
    if (!this.selectedServiceId) return;

    this.dailyCapacities.clear();

    const serviceData = this.availableTimeslots.find(s => s.serviceId === this.selectedServiceId);

    if (!serviceData?.slots) return;

    serviceData.slots.forEach(slot => {
      const date = slot.localTime.split(' ')[0];
      const currentCapacity = this.dailyCapacities.get(date) || 0;
      this.dailyCapacities.set(date, currentCapacity + (slot.capacity || 0));
    });
  }

  getDayCapacity(date: Date): number {
    const dateString = this.datepipe.transform(date, 'yyyy-MM-dd');
    return this.dailyCapacities.get(dateString) || 0;
  }

  hasNonExistingTimeSlot(date: Date): boolean {
    const dateString = this.datepipe.transform(date, 'yyyy-MM-dd');
    const serviceData = this.availableTimeslots.find(s => s.serviceId === this.selectedServiceId);
    if (!serviceData?.dates) {
      return false;
    }

    const dateInfo = serviceData.dates.find(d => d.date === dateString);
    const result = dateInfo?.planedForUnExistingTimeSlot || false;
    return result;
  }

  getTemplateCapacity(date: Date, templateStatus: string): number {
    if (!templateStatus) {
      return this.getDayCapacity(date);
    }

    if (typeof templateStatus === 'string' && templateStatus.toLowerCase() === 'closed') {
      return 0;
    }

    const template = this.additionalServiceTemplates.find(t => {
      const key = this.extractTemplateKey(t.name);
      return key && key.toLowerCase() === templateStatus.toLowerCase();
    });

    if (template?.slots) {
      return template.slots.reduce((total, slot) => total + ((slot.Capacity ?? slot.capacity) || 0), 0);
    }

    return this.getDayCapacity(date);
  }

  openEditAdditionalServiceModal(additionalService?: any) {
    if (this.additionalServices.length > 0) {
      var model: any = additionalService ? additionalService : null;
      const dialogRef = this.dialog.open(AdditionalServicesFormComponent, {
        maxHeight: '95vh',
        maxWidth: '95vw',
        width: '720px',
        hasBackdrop: true,
        data: {
          additionalService: model,
          organization: this.organization,
          edit: model ? true : false,
        }
      });
      dialogRef.afterOpened().subscribe(() => {
        const componentInstance = dialogRef.componentInstance as AdditionalServicesFormComponent;
        componentInstance.saveChangesEvent.subscribe((additionalService) => {
          this.loadData(false, true);
        });
      });
    }
  }

  async editAdditionalService(as, $event) {
    const dialogRef = this.dialog.open(BackOffice24OrdersEditAdditionalServiceComponent, {
      width: '720px',
      hasBackdrop: true,
      data: {
        additionalService: as,
        organization: this.organization,
        onlyCalendar: true
      }
    });
    dialogRef.afterOpened().subscribe(() => {
      const componentInstance = dialogRef.componentInstance as BackOffice24OrdersEditAdditionalServiceComponent;
      componentInstance.saveChangesEvent.subscribe((editedAdditionalService) => {
        this.loadData();
      });
    });
  }

  openTimeSlotsModal(): void {
    this.dialog.open(ScheduleTemplatesModalComponent, {
      width: '800px',
      hasBackdrop: true,
      data: {
        selectedServiceId: this.selectedServiceId,
        organization: this.organization,
        onTemplateUpdate: () => {
          this.loadScheduleTemplates();
        }
      }
    });
  }

  openUnplannedTasksModal(): void {
    this.dialog.open(UnplannedTasksModalComponent, {
      maxWidth: '95vw',
      hasBackdrop: true,
      data: {
        unplannedTasks: this.unplannedTasks,
        organization: this.organization,
        onTaskEdited: () => {
          this.reloadUnplannedTasks();
          this.loadData();
        }
      }
    });
  }

  private reloadUnplannedTasks(): void {
    try {
      this.loadUnplannedTasks().then(tasks => {
        this.unplannedTasks = tasks;
      });
    } catch (error) {
      console.error('Error reloading unplanned tasks:', error);
    }
  }

  refreshData() {
    this.loadData();
    this.reloadUnplannedTasks();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
    this.orgSubscription.unsubscribe();
  }
}
